import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { css, cx } from '@emotion/css';
import { groupBy, keyBy, map, reduce, some, uniq, values } from 'lodash/fp';

import { FlexColumn, FlexRow, ListItem, Popover, SvgIcon, Text } from '@portal/common/components';
import {
    CASE_HOSPITALIZATION_ID_KEY,
    CASE_OUTCOME_ID_KEY,
    CASE_SEVERITY_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    VALUE_KEY,
    YEAR_KEY,
} from '@portal/common/models/data-key';
import { TakedaDengueRecords } from '@portal/common/models/data-type';
import { styled } from '@portal/common/theme';
import { DataFilters, DataKey, DataRecord } from '@portal/common/types';
import { localizeFilterValues } from '@portal/common/utility/chart-data-helpers';
import formatChartValue from '@portal/common/utility/formatting/formatChartValue';

import _ from '../../../../locale';

const EXTRA_TITLES_PADDING = 24;

const Wrapper = styled(FlexRow)<{ width: number }>(({ theme }) => ({
    position: 'absolute',
    width: 'auto',
    top: EXTRA_TITLES_PADDING,
    zIndex: 10,
    flexWrap: 'wrap',
    ' > *': {
        fontSize: 16,
    },
}));

const StyledValue = styled(Text)(({ theme }) => ({
    color: theme.color.primary1,
    fontWeight: 900,
    fontSize: 16,
}));

const StyledShevronIcon = styled(SvgIcon)(({ theme }) => ({
    marginLeft: 4,
}));

const SelectedScenario = styled(Text)(({ theme }) => ({
    fontWeight: 900,
    cursor: 'pointer',
    textDecoration: 'underline',
    color: theme.color.primary1,
    ':hover': {
        svg: {
            fill: theme.color.primary1,
        },
    },
}));

const BaseScenario = styled(Text)(({ theme }) => ({
    fontWeight: 900,
    fontSize: 16,
}));

const poppoverContainerClassName = cx(css`
    display: inline-block;
`);

type Props = {
    filtersValues: DataFilters;
    baseForecastScenarioId: number;
    dataResponse: {
        records: Array<DataRecord>;
        columns: Array<DataKey>;
    };
    chartWidth: number;
    onHeightChange: (height: number) => void;
    timeRange: { start: string; end: string };
};

const calculateAvertedOutcome = ({
    records,
    columns,
    timeRange,
    scenarioA,
    scenarioB,
}: {
    records: Array<DataRecord>;
    columns: Array<DataKey>;
    timeRange: { start: string; end: string };
    scenarioA: number;
    scenarioB: number;
}) => {
    const yearIdx = columns.indexOf(YEAR_KEY);
    const forecastIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);
    const valueIdx = columns.indexOf(VALUE_KEY);

    if (some((idx) => idx === -1, [yearIdx, forecastIdx, valueIdx])) {
        return null;
    }

    if (!scenarioA || !scenarioB) {
        return null;
    }

    const filteredRecords =
        timeRange.start && timeRange.end
            ? records.filter((r) => r[yearIdx] >= timeRange.start && r[yearIdx] <= timeRange.end)
            : records;

    const summary = reduce(
        (sum, yearEntry) =>
            yearEntry[scenarioA] && yearEntry[scenarioB]
                ? sum + (yearEntry[scenarioA][valueIdx] - yearEntry[scenarioB][valueIdx])
                : sum,
        0,
        values(groupBy((r) => r[yearIdx], filteredRecords)).map((yearRecords) =>
            keyBy((r) => r[forecastIdx], yearRecords)
        )
    );

    return summary;
};

const localizeDengueFilterValue = (filter: DataKey, filtersValues: DataFilters) =>
    localizeFilterValues(filter, filtersValues[filter], TakedaDengueRecords);

const localizeForecastScenario = (scenarioId: number) => _(`forecast_scenario_${scenarioId}`);

const AvertedOutcomeSubheading: FC<Props> = ({
    dataResponse,
    baseForecastScenarioId,
    filtersValues,
    timeRange = {},
    chartWidth,
    onHeightChange,
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const caseOutcome = localizeDengueFilterValue(CASE_OUTCOME_ID_KEY, filtersValues);
    const caseHospitalization = localizeDengueFilterValue(
        CASE_HOSPITALIZATION_ID_KEY,
        filtersValues
    );
    const caseSeverity = localizeDengueFilterValue(CASE_SEVERITY_ID_KEY, filtersValues);

    const scenarios = useMemo(() => {
        const forecastScenarioIdx = dataResponse.columns.findIndex(
            (column) => column === FORECAST_SCENARIO_ID_KEY
        );
        const scenarios: number[] = uniq(
            map((record, index) => record[forecastScenarioIdx] || index, dataResponse.records)
        ).filter((scenarioId) => scenarioId !== baseForecastScenarioId);
        return scenarios;
    }, [dataResponse]);
    const [selectedScenario, setSelectedScenario] = React.useState<number>(scenarios[0]);
    const value = useMemo(
        () =>
            formatChartValue(
                calculateAvertedOutcome({
                    records: dataResponse.records,
                    columns: dataResponse.columns,
                    timeRange,
                    scenarioA: baseForecastScenarioId,
                    scenarioB: selectedScenario,
                })
            ),
        [dataResponse, timeRange, selectedScenario, baseForecastScenarioId]
    );
    const renderContent = ({ toggleVisibility }) => {
        const selectScenario = (scenarioId) => {
            setSelectedScenario(scenarioId);
            toggleVisibility(false);
        };

        return (
            <FlexColumn>
                {scenarios.map((scenarioId) => (
                    <ListItem
                        key={scenarioId}
                        isSelected={selectedScenario === scenarioId}
                        onClick={selectScenario.bind(null, scenarioId)}
                    >
                        <div>{localizeForecastScenario(scenarioId)}&nbsp;</div>
                    </ListItem>
                ))}
            </FlexColumn>
        );
    };

    const openDropdown = useCallback((toggleVisibility) => {
        toggleVisibility(true);
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            onHeightChange(containerRef.current.clientHeight + EXTRA_TITLES_PADDING);
        }
    }, [chartWidth]);
    const fullValueString = `${value} ${caseSeverity} ${caseHospitalization} ${caseOutcome}`;

    return (
        <Wrapper align="center" ref={containerRef}>
            <StyledValue>{fullValueString}</StyledValue>
            <span>&nbsp;averted by&nbsp;</span>
            <Popover
                disableHideOnInnerClick
                renderContent={renderContent}
                containerClassName={poppoverContainerClassName}
            >
                {({ ref, toggleVisibility, visibility }) => (
                    <SelectedScenario ref={ref} onClick={openDropdown.bind(null, toggleVisibility)}>
                        {localizeForecastScenario(selectedScenario)}
                        <StyledShevronIcon
                            src={visibility ? '/icons/ic-arrow-up.svg' : '/icons/ic-arrow-down.svg'}
                            color={(theme) => theme.color.success}
                        />
                    </SelectedScenario>
                )}
            </Popover>
            <span>&nbsp;compared to&nbsp;</span>
            <BaseScenario>{localizeForecastScenario(baseForecastScenarioId)}</BaseScenario>
        </Wrapper>
    );
};

export default AvertedOutcomeSubheading;
